/**
 * OPS-11: Quick Metrics Panel
 *
 * Compact row of key operational indicators derived from alerts and
 * shift briefing data. Provides at-a-glance operational awareness.
 */

import { memo } from 'react';
import type { ControlTowerState } from '../../services/controlTowerTypes';
import { InfoTooltip } from './InfoTooltip';

interface MetricCardProps {
  label: string;
  value: number;
  icon: string;
  color: string;
  tooltip: string;
}

function MetricCard({ label, value, icon, color, tooltip }: MetricCardProps) {
  return (
    <div className="rounded-xl bg-slate-800/50 border border-slate-700/50 p-3 flex items-center gap-2.5 min-w-0">
      <div className={`w-8 h-8 rounded-lg ${color} flex-shrink-0 flex items-center justify-center text-base`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-lg font-bold text-slate-100 leading-tight">{value}</p>
        <div className="flex items-center gap-1">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide truncate">{label}</p>
          <InfoTooltip text={tooltip} />
        </div>
      </div>
    </div>
  );
}

interface QuickMetricsPanelProps {
  state: ControlTowerState;
}

export const QuickMetricsPanel = memo(function QuickMetricsPanel({ state }: QuickMetricsPanelProps) {
  const alertCount = state.alerts.length;
  const criticalAlerts = state.alerts.filter((a) => a.severity === 'critical').length;
  const riskCount = state.guestRadar.risks.length;
  const highlightCount = state.shiftBriefing.highlights.length;
  const actionCount = state.shiftBriefing.recommendedActions.filter((a) => a.type !== 'NO_ACTION_REQUIRED').length;
  const factorCount = state.guestRadar.factors.length;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
      <MetricCard label="Active Alerts" value={alertCount} icon="🔔" color="bg-amber-500/10" tooltip="Total number of operational alerts currently active across all severity levels." />
      <MetricCard label="Critical" value={criticalAlerts} icon="🚨" color="bg-rose-500/10" tooltip="Alerts at critical severity requiring immediate staff intervention." />
      <MetricCard label="Guest Risks" value={riskCount} icon="👤" color="bg-orange-500/10" tooltip="Number of active guest experience risks detected by the Guest Radar system." />
      <MetricCard label="Highlights" value={highlightCount} icon="📋" color="bg-sky-500/10" tooltip="Key operational observations identified by the AI shift briefing engine." />
      <MetricCard label="Actions" value={actionCount} icon="⚡" color="bg-violet-500/10" tooltip="AI-recommended actions for the current shift based on operational conditions." />
      <MetricCard label="GX Factors" value={factorCount} icon="📊" color="bg-teal-500/10" tooltip="Guest experience scoring factors — the weighted components that drive the overall GX score." />
    </div>
  );
});
